<template>
  <VDialog
    v-model="_dialog"
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
    scrollable
  >
    <VCard tile>
      <VToolbar
        card
        dark
        color="primary"
      >
        <VBtn
          icon
          dark
          @click="_dialog = false"
        >
          <VIcon>
            close
          </VIcon>
        </VBtn>
        <VToolbarTitle>{{ name }} Followup Rules</VToolbarTitle>
        <VSpacer />
        <VToolbarItems>
          <VBtn
            dark
            flat
            @click="onSaveClicked"
          >
            save
          </VBtn>
        </VToolbarItems>
      </VToolbar>
      <VCardText>
        <VItemGroup>
          <VContainer grild-list-md>
            <VLayout wrap>
              <VFlex
                v-for="(item, index) in allRules"
                :key="index"
                xs12
                sm6
                md4
                pa-2
              >
                <VItem>
                  <RuleDialogCard
                    :rule-index="index"
                    :is-rule-enabled="isRuleEnabled"
                    :set-rule-enabled="setRuleEnabled"
                    :show-details="showDetails"
                    :apply-to-new-option="applyToNewOption"
                    :update-rule-gql="updateRuleGql"
                    :create-rule-gql="createRuleGql"
                    :detail-dialog.sync="detailDialog"
                    :detail-dialog-title.sync="detailDialogTitle"
                    :save-method.sync="saveMethod"
                    :save-method-gql.sync="saveMethodGql"
                    :edit-rule-index.sync="editRuleIndex"
                    :rule-data.sync="ruleData"
                  />
                </VItem>
              </VFlex>
            </VLayout>
          </VContainer>
        </VItemGroup>
      </VCardText>
    </VCard>
    <VBtn
      fab
      bottom
      right
      color="pink"
      dark
      fixed
      @click.stop="onDetailDialogOpen('Add New Rule')"
    >
      <VIcon>add</VIcon>
    </VBtn>
    <ProgressDialogCard
      :description="statusMessage"
      :dialog="loadingStatus"
      indeterminate
    />
    <FollowupDetailDialogCard
      :dialog.sync="detailDialog"
      :title-name="detailDialogTitle"
      :save-method="saveMethod"
      :save-method-gql="saveMethodGql"
      :rule-type-dict="ruleTypeDict"
      :rule-data="ruleData"
      :rule-index="editRuleIndex"
      :is-rule-enabled="isRuleEnabled"
      :set-rule-enabled="setRuleEnabled"
      :apply-to-new-option="applyToNewOption"
      :apply-to-all-option="applyToAllOption"
      :rule-holder-id="ruleHolderId"
    />
  </VDialog>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { automationMapActions } from '@/store/utils'
import { NetworkStatus } from 'apollo-client/core/networkStatus'
import ProgressDialogCard from '@/components/ProgressDialogCard'
import _ from 'lodash'
import FollowupDetailDialogCard from './FollowupDetailDialogCard'
import { CREATE_PROFILE_RULE, LIST_PROFILE_RULES, UPDATE_PROFILE_RULE } from '@/backend/graphql/automationGQL'
import RuleDialogCard from './RuleDialogCard'
export default {
  name: 'BaseFollowupRuleDialog',
  components: {
    RuleDialogCard,
    FollowupDetailDialogCard,
    ProgressDialogCard
  },
  props: {
    name: {
      type: String,
      default: ''
    },
    ruleHolderId: {
      type: Number,
      default: null
    },
    dialog: {
      type: Boolean,
      default: false
    },
    applyToNewOption: {
      type: Boolean,
      default: false
    },
    applyToAllOption: {
      type: Boolean,
      default: false
    },
    showDetails: {
      type: Boolean,
      default: true
    },
    queryRuleGql: {
      type: Object,
      default: function () {
        return LIST_PROFILE_RULES
      }
    },
    updateRuleGql: {
      type: Object,
      default: function () {
        return UPDATE_PROFILE_RULE
      }
    },
    createRuleGql: {
      type: Object,
      default: function () {
        return CREATE_PROFILE_RULE
      }
    },
    getQueryVariables: {
      type: Function,
      default: function () {
        return {}
      }
    },
    isRuleEnabled: {
      type: Function,
      default: function (index) {}
    },
    setRuleEnabled: {
      type: Function,
      default: function (index) {}
    },
    defaultRuleData: {
      type: Object,
      default: function () {
        return {}
      }
    }
  },
  apollo: {
    rules: {
      query () {
        return this.queryRuleGql
      },
      variables () {
        return this.getQueryVariables()
      },
      manual: true,
      result ({ data, loading, networkStatus }) {
        this.setNetworkStatus(networkStatus)
        if (!loading && networkStatus !== NetworkStatus.error) {
          this.parseData(data)
          this.setQueryStatus({ val: false })
        }
      },
      error (error) {
        this.setQueryStatus({ val: false })
        this.$store.dispatch('alert/error', error.message)
      }
    }
  },
  data () {
    return {
      mask: '###',
      ruleTypeDict: {
        STAY_IN_CONTACT: 'Stay in contact',
        EVENT_FOLLOWUP: 'Event followup'
      },
      maxNumber: 999,
      minNumber: -999,
      detailDialog: false,
      detailDialogTitle: '',
      menuItems: [
        { title: 'Edit' },
        { title: 'Delete' }
      ],
      ruleData: {},
      saveMethod: function () {},
      saveMethodGql: {},
      editRuleIndex: null
    }
  },
  computed: {
    ...mapGetters({
      loadingStatus: 'automation/getLoadingStatus',
      statusMessage: 'automation/getLoadingText',
      allRules: 'automation/getAllRules',
      enabledByIndex: 'automation/getEnabledByIndex'
    }),
    _dialog: {
      get () {
        return this.dialog
      },
      set (val) {
        this.$emit('update:dialog', val)
      }
    }
  },
  beforeDestroy () {
    this.clearRules()
  },
  methods: {
    ...mapActions(Object.assign(automationMapActions, {})),
    getItems (data) { return _.get(data, 'listUserRules.results', []) },
    getTotalItemsCount (data) { return _.get(data, 'listUserRules.totalCount', 0) },
    parseData (data) {
      if (this.allRules.length === 0) {
        this.initRules({
          newRules: this.getItems(data),
          totalCount: this.getTotalItemsCount(data)
        })
      } else {
        this.appendRules({
          newRules: this.getItems(data),
          startIndex: this.offset
        })
      }
    },
    setNetworkStatus (networkStatus) {
      switch (networkStatus) {
        case NetworkStatus.loading:
          this.setQueryStatus({ val: true })
          break
        case NetworkStatus.setVariables:
          this.setQueryStatus({ val: true })
          this.setQueryStatusMessage({ message: 'Loading Rules. Please wait ...' })
          break
        case NetworkStatus.ready:
          break
        case NetworkStatus.poll:
          break
        case NetworkStatus.error:
          this.setQueryStatus({ val: false })
          break
        default:
          break
      }
    },
    onSaveClicked () {
      this._dialog = false
    },
    onDetailDialogOpen (titleName, data) {
      this.detailDialogTitle = titleName
      this.ruleData = data || this.defaultRuleData
      this.editRuleIndex = null
      this.saveMethod = this.createRule
      this.saveMethodGql = this.createRuleGql
      this.detailDialog = true
    }
  }
}
</script>

<style scoped>
</style>
